import { Handle, Position } from '@xyflow/react';
import { LuTag, LuArrowRight } from 'react-icons/lu';

// A small grab handle on each side. With connectionMode="loose" (set on the canvas) any handle can act as
// both source and target, so a single handle per side is enough to drag a connection from / drop one onto any
// edge of the node. Edges themselves are drawn floating (clipped to the border), not anchored to these.
const HANDLE_SIDES = [
  { id: 'top', position: Position.Top },
  { id: 'right', position: Position.Right },
  { id: 'bottom', position: Position.Bottom },
  { id: 'left', position: Position.Left },
];

/**
 * GraphVertexNode — custom React Flow node for graph vertices.
 *
 * Two rows: the bold label (joined labels) and a muted rev line. Small grab handles sit on all four sides,
 * hidden until the node is hovered/selected (styled in index.css) so they don't clutter the canvas.
 *
 * In subset mode a frontier vertex (one that may still have unloaded neighbors) is flagged via
 * data.canExpand: the node wrapper is subtly tinted (see index.css) and a small traversal control carrying a
 * → arrow is anchored to the node's bottom-right corner and loads the vertex's neighbors. Double-click on the
 * node body does the same; the arrow control is the explicit affordance so it is obvious which vertices can
 * still be traversed.
 */
function GraphVertexNode({ data, isConnectable }) {
  const rev = data?.element?.rev;
  const hasRev = rev !== undefined && rev !== null;
  const canExpand = !!data.canExpand;
  return (
    <div className="graph-vertex-node">
      <div className="graph-vertex-node-row">
        <LuTag size={9} className="graph-vertex-node-icon" />
        <span className="graph-vertex-node-label">{data.label}</span>
      </div>
      {hasRev && (
        <div className="graph-vertex-node-rev">rev {rev}</div>
      )}
      {canExpand && (
        <button
          type="button"
          className="graph-vertex-node-expand nodrag nopan"
          title="Load this vertex's neighbors"
          aria-label="Expand neighbors"
          onClick={(event) => {
            event.stopPropagation();
            if (data.onExpand) data.onExpand();
          }}
        >
          <LuArrowRight size={5.76} strokeWidth={2.75} />
        </button>
      )}
      {HANDLE_SIDES.map(side => (
        <Handle
          key={side.id}
          type="source"
          position={side.position}
          id={side.id}
          className="graph-vertex-handle"
          isConnectable={isConnectable}
        />
      ))}
    </div>
  );
}

export default GraphVertexNode;
